import * as React from 'react';
import { storiesOf } from '@storybook/react';
import { VisualTestContainer } from '../../test/visual/VisualTestContainer';
import { TextArea, TextAreaProps, TextAreaTheme } from '..';
import { TPAComponentsProvider } from '../TPAComponentsConfig';
import { classes } from './TextArea.visual.st.css';
import { ErrorAppearance } from '../internal/ErrorMessageWrapper';

interface TextAreaVisualProps extends TextAreaProps {
  rtl: boolean;
}
class TextAreaVisual extends React.Component<TextAreaVisualProps> {
  static defaultProps: TextAreaVisualProps = {
    placeholder: 'Placeholder',
    ariaLabel: 'Test',
    onChange: () => {},
    value:
      'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.',
    rtl: false,
  };
  render() {
    const { rtl, ...rest } = this.props;
    return (
      <VisualTestContainer>
        <div dir={rtl ? 'rtl' : 'ltr'}>
          <TPAComponentsProvider value={{ rtl }}>
            <TextArea {...rest} />
          </TPAComponentsProvider>
          {this.props.newErrorMessage && (
            <div className={classes.elementBelow}>
              An element below to show the spacing
            </div>
          )}
        </div>
      </VisualTestContainer>
    );
  }
}

interface Test {
  describe: string;
  its: {
    it: string;
    props: Partial<TextAreaProps>;
    style?: Record<string, string>;
  }[];
}

const tests: Test[] = [
  {
    describe: 'TextArea',
    its: [
      {
        it: 'label when textarea disabled',
        props: {
          disabled: true,
          value: '',
          label: 'some-label',
          placeholder: 'Placeholder',
        },
      },
      {
        it: 'label',
        props: {
          value: '',
          label: 'some-label',
          placeholder: 'Placeholder',
        },
      },
      {
        it: 'label with asterisk',
        props: {
          value: '',
          label: 'some-label',
          required: true,
          showAsterisk: true,
        },
      },
      {
        it: 'placeholder',
        props: {
          value: '',
          placeholder: 'Placeholder',
        },
      },
      {
        it: 'text',
        props: {
          value:
            'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.',
        },
      },
      {
        it: 'disabled',
        props: {
          disabled: true,
        },
      },
      {
        it: 'error',
        props: {
          error: true,
        },
      },
      {
        it: 'error with description',
        props: {
          error: true,
          errorMessage: 'Something went wrong',
        },
      },
      {
        it: 'New error message without a description',
        props: {
          error: true,
          newErrorMessage: true,
        },
      },
      {
        it: 'New error message without a description / ErrorAppearance - BackgroundAndBorder',
        props: {
          errorAppearance: ErrorAppearance.BackgroundAndBorder,
          error: true,
          newErrorMessage: true,
        },
      },
      {
        it: 'New error message with description',
        props: {
          error: true,
          errorMessage: 'This is an error message',
          newErrorMessage: true,
        },
      },
      {
        it: 'New error message with description / ErrorAppearance - BackgroundAndBorder',
        props: {
          errorAppearance: ErrorAppearance.BackgroundAndBorder,
          error: true,
          errorMessage: 'This is an error message',
          newErrorMessage: true,
        },
      },
      {
        it: 'New error message with override styles',
        props: {
          error: true,
          newErrorMessage: true,
          className: classes.root,
        },
      },
      {
        it: 'New error message with override styles / ErrorAppearance - BackgroundAndBorder',
        props: {
          errorAppearance: ErrorAppearance.BackgroundAndBorder,
          error: true,
          newErrorMessage: true,
          className: classes.root,
        },
      },
      {
        it: 'New error message with description and override styles',
        props: {
          error: true,
          errorMessage: 'This is an error message',
          newErrorMessage: true,
          className: classes.root,
        },
      },
      {
        it: 'New error message with description and override styles / ErrorAppearance - BackgroundAndBorder',
        props: {
          errorAppearance: ErrorAppearance.BackgroundAndBorder,
          error: true,
          errorMessage: 'This is an error message',
          newErrorMessage: true,
          className: classes.root,
        },
      },
      {
        it: 'success',
        props: {
          success: true,
        },
      },
      {
        it: 'theme line',
        props: {
          theme: TextAreaTheme.Line,
        },
      },
      {
        it: 'theme box',
        props: {
          theme: TextAreaTheme.Box,
        },
      },
      {
        it: 'char count',
        props: {
          value: 'Lorem',
          label: 'some-label',
          showCharCount: true,
          maxLength: 10,
        },
      },
      {
        it: 'withResize',
        props: {
          value: 'Lorem',
          label: 'some-label',
          withResize: true,
        },
      },
    ],
  },
];

const styleOverridesTests: Test[] = [
  {
    describe: 'TextArea style override',
    its: [
      {
        it: 'with simple style overrides',
        props: {
          value: 'Lorem',
          label: 'label',
          className: classes.simple,
          showCharCount: true,
          maxLength: 20,
        },
      },
      {
        it: 'with placeholder style overrides',
        props: {
          placeholder: 'placeholder',
          value: '',
          className: classes.placeholder,
        },
      },
      {
        it: 'with disabled style overrides',
        props: {
          label: 'label',
          className: classes.disabled,
          disabled: true,
        },
      },
      {
        it: 'with error style overrides',
        props: {
          className: classes.error,
          error: true,
          newErrorMessage: true,
          errorMessage: 'something went wrong',
        },
      },
      {
        it: 'with error style overrides / ErrorAppearance - BackgroundAndBorder',
        props: {
          errorAppearance: ErrorAppearance.BackgroundAndBorder,
          className: classes.error,
          error: true,
          newErrorMessage: true,
          errorMessage: 'something went wrong',
        },
      },
      {
        it: 'with success style overrides',
        props: {
          className: classes.success,
          success: true,
        },
      },
      {
        it: 'with line style overrides',
        props: {
          className: classes.line,
          theme: TextAreaTheme.Line,
        },
      },
      {
        it: 'line with hover style overrides',
        props: {
          className: classes.hoverOverrides,
          theme: TextAreaTheme.Line,
          value: 'Line with hover override',
        },
      },
      {
        it: 'box with hover style overrides',
        props: {
          className: classes.hoverOverrides,
          theme: TextAreaTheme.Box,
          value: 'Box with hover override',
        },
      },
      {
        it: 'with start padding',
        props: {
          value: 'Lorem',
          label: 'some-label',
          className: classes.startPadding,
        },
      },
      {
        it: 'box with vertical padding',
        props: {
          className: classes.verticalPadding,
          value:
            'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.',
        },
      },
      {
        it: 'box with horizontal padding',
        props: {
          className: classes.horizontalPadding,
          value:
            'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.',
        },
      },
    ],
  },
  {
    describe: 'TextArea with WST theming',
    its: [
      {
        it: 'default',
        props: {
          label: 'label',
          maxLength: 50,
          showCharCount: true,
          value:
            'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.',
        },
        style: {
          '--wst-primary-background-color-rgb': '180,250,195',
          '--wst-paragraph-2-color-rgb': '255,120,120',
          '--wst-paragraph-2-font-family': 'Libre Baskerville',
          '--wst-shade-3-color-rgb': '170,70,110',
        },
      },
      {
        it: 'font',
        props: {
          label: 'label',
          maxLength: 50,
          showCharCount: true,
          value:
            'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.',
        },
        style: {
          '--wst-primary-background-color-rgb': '180,250,195',
          '--wst-paragraph-2-color-rgb': '255,120,120',
          '--wst-paragraph-2-font-family': 'Libre Baskerville',
          '--wst-paragraph-2-font-size': '22px',
          '--wst-shade-3-color-rgb': '170,70,110',
          '--wst-paragraph-2-font-weight': 'bold',
          '--wst-paragraph-2-font-style': 'italic',
          '--wst-paragraph-2-font-variant': 'small-caps',
        },
      },
      {
        it: 'placeholder',
        props: {
          placeholder: 'Some placeholder',
          value: '',
        },
        style: {
          '--wst-paragraph-2-font-family': 'Libre Baskerville',
          '--wst-shade-3-color-rgb': '10,180,110',
        },
      },
      {
        it: 'success state',
        props: {
          success: true,
          value:
            'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.',
        },
        style: {
          '--wst-paragraph-2-color-rgb': '0,155,255',
          '--wst-paragraph-2-font-family': 'Libre Baskerville',
          '--wst-system-success-color-rgb': '230,180, 0',
        },
      },
      {
        it: 'error state',
        props: {
          error: true,
          errorMessage: 'something went wrong',
          errorAppearance: ErrorAppearance.BackgroundAndBorder,
        },
        style: {
          '--wst-system-error-color-rgb': '10,100,255',
        },
      },
      {
        it: 'error state with newErrorMessage',
        props: {
          newErrorMessage: true,
          error: true,
          errorMessage: 'something went wrong',
          errorAppearance: ErrorAppearance.BackgroundAndBorder,
        },
        style: {
          '--wst-system-error-color-rgb': '10,100,255',
        },
      },
      {
        it: 'disabled state',
        props: {
          disabled: true,
        },
        style: {
          '--wst-system-disabled-color-rgb': '0,180,80',
        },
      },
    ],
  },
];

tests.forEach(({ describe, its }) => {
  its.forEach(({ it, props }) => {
    storiesOf(`TextArea/${describe}`, module).add(it, () => (
      <TextAreaVisual {...props} />
    ));
    storiesOf(`TextArea/${describe}`, module).add(`rtl/${it}`, () => (
      <TextAreaVisual {...props} rtl />
    ));
  });
});

styleOverridesTests.forEach(({ describe, its }) => {
  its.forEach(({ it, props, style }) => {
    storiesOf(`TextArea/${describe}`, module).add(it, () => (
      <div style={style}>
        <TextAreaVisual {...props} />
      </div>
    ));
  });
});
